import React, { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { rememberReturnPath } from '@/lib/authReturnPath';
import { useMyAdminModuleAccess } from '@/lib/adminModuleAccess';
import { normalizeRole } from '@/lib/roles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  /**
   * RBAC admin-module key that also unlocks this route (Role & Access panel
   * grants): a user failing the allowedRoles check still passes when their
   * effective access to this module meets `adminModuleMinLevel`.
   */
  adminModuleKey?: string;
  /** Minimum grant level for adminModuleKey (default 'read'). Write-capable pages pass 'full'. */
  adminModuleMinLevel?: 'read' | 'full';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = [],
  adminModuleKey,
  adminModuleMinLevel = 'read',
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { moduleAccess, isLoading: accessLoading } = useMyAdminModuleAccess();
  const [_, setLocation] = useLocation();
  const { toast } = useToast();

  const grantLevel = adminModuleKey ? moduleAccess[adminModuleKey] : undefined;
  const hasModuleGrant =
    grantLevel === 'full' || (grantLevel === 'read' && adminModuleMinLevel === 'read');
  // Don't bounce while the grant lookup is still in flight.
  const waitingOnGrant = !!adminModuleKey && accessLoading;

  useEffect(() => {
    // Wait until auth is checked
    if (isLoading) return;

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      rememberReturnPath();
      toast({
        title: 'Authentication required',
        description: 'Please log in to access this page',
        variant: 'destructive',
      });
      setLocation('/login');
      return;
    }

    // If role-based access is required
    if (allowedRoles.length > 0 && user && !waitingOnGrant) {
      if (!allowedRoles.includes(normalizeRole(user.role)) && !hasModuleGrant) {
        toast({
          title: 'Access denied',
          description: 'You do not have permission to access this page',
          variant: 'destructive',
        });
        setLocation('/');
        return;
      }
    }
  }, [
    isAuthenticated,
    isLoading,
    user,
    setLocation,
    toast,
    allowedRoles,
    hasModuleGrant,
    waitingOnGrant,
  ]);

  // Show nothing while checking authentication or a pending grant lookup
  if (isLoading || waitingOnGrant) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  // Show content only if authenticated
  return isAuthenticated ? <>{children}</> : null;
};

export default ProtectedRoute;
